import { Download } from "lucide-react";
import PropTypes from "prop-types";
import Datatable from "@components/datatable";
import LoadingButton from "@components/ui-components/loading-button";
import StatusChip from "@components/ui-components/status-chip";
import { formatDate } from "@helpers/date";
import SignaturesCell from "@panel/shared/contract/components/signatures-cell";
import {
    PUBLISHER_CONTRACT_STATUS,
    PUBLISHER_CONTRACT_STATUS_LABEL,
    PUBLISHER_CONTRACT_STATUS_TONE
} from "@panel/shared/contract/misc/constants";

// One row per brand the publisher is mapped to. Paging is client-side — the
// account has one contract, so this is a single row.
const ContractsTable = ({ contracts, loading, onReview, onDownload, downloading = false }) => {
    const columns = [
        {
            Header  : "Brand",
            accessor: "brandName",
            Cell    : ({ value }) => <span className="font-medium text-gray-900">{value || "-"}</span>
        },
        {
            Header  : "Contract",
            accessor: "name",
            Cell    : ({ value }) => <span className="text-gray-700">{value}</span>
        },
        {
            Header  : "Version",
            accessor: "version",
            Cell    : ({ value }) => <span className="text-gray-700">{value === null ? "-" : `v${value}`}</span>
        },
        {
            Header  : "Status",
            accessor: "status",
            Cell    : ({ value }) => (
                <StatusChip tone={PUBLISHER_CONTRACT_STATUS_TONE[value]} label={PUBLISHER_CONTRACT_STATUS_LABEL[value]} />
            )
        },
        {
            Header  : "Signatures",
            accessor: "signatures",
            Cell    : ({ value }) => <SignaturesCell signatures={value} />
        },
        {
            Header  : "Signed on",
            accessor: "signedAt",
            Cell    : ({ value }) => <span className="text-gray-700">{value ? formatDate(value) : "—"}</span>
        },
        {
            Header  : "Action",
            accessor: "id",
            Cell    : ({ row }) => (
                <div className="flex items-center justify-end gap-2">
                    <LoadingButton
                        color="neutral"
                        size="xs"
                        icon={Download}
                        text="Download"
                        loading={downloading}
                        onClick={() => onDownload(row)}
                        testId="download-contract" />
                    <LoadingButton
                        color="dark"
                        size="xs"
                        // A signed contract stays openable so the publisher
                        // can read back what they agreed to.
                        text={row.status === PUBLISHER_CONTRACT_STATUS.SIGNED ? "View contract" : "Review contract"}
                        onClick={() => onReview(row)}
                        testId="review-contract" />
                </div>
            )
        }
    ];

    return (
        <section className="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <h2 className="border-b border-gray-200 px-5 py-4 text-base font-semibold text-gray-900">Brand contracts</h2>
            <Datatable
                columns={columns}
                data={contracts}
                loading={loading}
                customNoData={<span className="text-sm text-muted">No contract has been sent to you yet.</span>} />
        </section>
    );
};

ContractsTable.propTypes = {
    contracts  : PropTypes.array,
    loading    : PropTypes.bool,
    onReview   : PropTypes.func.isRequired,
    onDownload : PropTypes.func.isRequired,
    downloading: PropTypes.bool
};

export default ContractsTable;
